import { useCallback, useEffect, useRef, useState } from 'react';
import { getScenario } from '../../../shared/api/scenarioApi';
import { useScenario } from '../../../shared/contexts/ScenarioContext';

export interface ShipViewDeltaMaps {
  /** voyageId (or delta.id for CREATED) → set of changed field names from deltaJson */
  voyageDeltaFields: Record<string, Set<string>>;
  /** voyageId → 'CREATED' | 'MODIFIED' | 'CANCELLED' */
  voyageChangeTypes: Record<string, string>;
  /** shipmentId (or delta.id for CREATED) → set of changed field names */
  shipmentDeltaFields: Record<string, Set<string>>;
  /** shipmentId → 'CREATED' | 'MODIFIED' | 'CANCELLED' */
  shipmentChangeTypes: Record<string, string>;
  /** Trigger a re-fetch of the scenario deltas */
  refresh: () => void;
}

const EMPTY_RECORD: Record<string, Set<string>> = {};
const EMPTY_CHANGE_TYPES: Record<string, string> = {};

const useShipViewScenarioDeltas = (): ShipViewDeltaMaps => {
  const { activeScenario } = useScenario();

  const [voyageDeltaFields, setVoyageDeltaFields] =
    useState<Record<string, Set<string>>>(EMPTY_RECORD);
  const [voyageChangeTypes, setVoyageChangeTypes] =
    useState<Record<string, string>>(EMPTY_CHANGE_TYPES);
  const [shipmentDeltaFields, setShipmentDeltaFields] =
    useState<Record<string, Set<string>>>(EMPTY_RECORD);
  const [shipmentChangeTypes, setShipmentChangeTypes] =
    useState<Record<string, string>>(EMPTY_CHANGE_TYPES);

  const fetchIdRef = useRef(0);

  const fetchDeltas = useCallback(async () => {
    if (!activeScenario) {
      setVoyageDeltaFields(EMPTY_RECORD);
      setVoyageChangeTypes(EMPTY_CHANGE_TYPES);
      setShipmentDeltaFields(EMPTY_RECORD);
      setShipmentChangeTypes(EMPTY_CHANGE_TYPES);
      return;
    }

    const currentFetchId = ++fetchIdRef.current;

    try {
      const dto = await getScenario(activeScenario.id);
      if (currentFetchId !== fetchIdRef.current) return; // stale

      const vFields: Record<string, Set<string>> = {};
      const vTypes: Record<string, string> = {};

      for (const d of dto.voyageDeltas ?? []) {
        // MODIFIED/CANCELLED use voyageId; CREATED use delta's own id
        const targetId = d.voyageId ?? d.id;
        vTypes[targetId] = d.changeType;
        if (d.deltaJson) {
          vFields[targetId] = new Set(Object.keys(d.deltaJson));
        } else if (d.changeType === 'CREATED') {
          // CREATED without deltaJson — mark as fully new
          vFields[targetId] = new Set(['__created__']);
        }
      }

      const sFields: Record<string, Set<string>> = {};
      const sTypes: Record<string, string> = {};

      for (const d of dto.shipmentDeltas ?? []) {
        const targetId = d.shipmentId ?? d.id;
        sTypes[targetId] = d.changeType;
        if (d.deltaJson) {
          sFields[targetId] = new Set(Object.keys(d.deltaJson));
        } else if (d.changeType === 'CREATED') {
          sFields[targetId] = new Set(['__created__']);
        }
      }

      setVoyageDeltaFields(vFields);
      setVoyageChangeTypes(vTypes);
      setShipmentDeltaFields(sFields);
      setShipmentChangeTypes(sTypes);
    } catch {
      // Silently fail — highlighting is best-effort
    }
  }, [activeScenario]);

  useEffect(() => {
    fetchDeltas();
  }, [fetchDeltas]);

  return {
    voyageDeltaFields,
    voyageChangeTypes,
    shipmentDeltaFields,
    shipmentChangeTypes,
    refresh: fetchDeltas,
  };
};

export default useShipViewScenarioDeltas;
